import { GameStatuses, type ScoreboardResponse } from '@on3/api';
import {
  type GameTeamData,
  getGameTime,
  getPredictionOutcome,
  getTeamData,
} from '@on3/api/utils/games';
import { Check, XMark } from '@on3/ui-lib/components/Svg';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { handleKeyDown } from '@on3/ui-lib/utils/accessibility/keyDown';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { GameWrapper } from '@public/hocs/Game/GameWrapper';
import clsx from 'clsx';
import { format, parse } from 'date-fns';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useCallback } from 'react';

import styles from './ScoreTile.module.scss';

const decimalToPercentage = (decimal: number | null | undefined) => {
  if (!decimal) return '-';

  return `${Math.round(decimal * 100)}%`;
};

export const ScoreTile = ({
  game,
  sport,
}: {
  game: ScoreboardResponse;
  sport: string;
}) => {
  const { state = 'national' } = useParams() as { state?: string };

  const {
    currentOrgKey,
    currentOrgIsWinner,
    currentOrgIsHome,
    startDate,
    statusEnum,
    winner,
  } = game || {};

  const parsedDate = game?.startDateUtc
    ? new Date(game.startDateUtc * 1000)
    : startDate && parse(startDate, 'M/d', new Date());

  const gameDate = parsedDate && format(parsedDate, 'EEE M/d');
  const gameTime = getGameTime(game);

  const homeTeam = getTeamData(true, game);
  const awayTeam = getTeamData(false, game);
  const currentTeam = currentOrgIsHome ? homeTeam : awayTeam;

  const primaryColor = adjustColorForReadability(
    game.currentOrganization?.primaryColor ?? '#fe3b1f',
  );

  const predictionOutcome = getPredictionOutcome(game);

  const getScoreStyle = (team: { key?: number }) => {
    if (
      !currentOrgKey ||
      team.key !== currentTeam.key ||
      statusEnum !== GameStatuses.Completed
    ) {
      return;
    }

    return currentOrgIsWinner ? styles.colorWin : styles.colorLoss;
  };

  const getRank = useCallback(
    (team: GameTeamData) => {
      if (state === 'national') return team.ranking.national;

      return team.ranking.state;
    },
    [state],
  );

  const showRank = (ranking?: number | null) => {
    if (!ranking || ranking > 50) return false;

    return true;
  };

  return (
    <GameWrapper
      game={{
        ...game,
        currentTeam: {
          key: currentTeam.key as number,
          name: currentTeam.name as string,
          primaryColor,
        },
      }}
    >
      {(gameActions) => (
        <article
          className={styles.block}
          data-status={statusEnum}
          data-winner={winner?.key === homeTeam.key ? 'home' : 'away'}
        >
          <header>
            <div
              className={clsx(styles.date, {
                [styles.final]: statusEnum === GameStatuses.Completed,
                [styles.inProgress]: statusEnum === GameStatuses.InProgress,
                [styles.delayed]: statusEnum === GameStatuses.Delayed,
              })}
            >
              <span>{gameDate}</span>
              <span>{gameTime}</span>
            </div>
            <div>Score</div>
            <div>Prediction</div>
          </header>
          <section>
            <div className={styles.teams}>
              <div className={styles.away}>
                <span className={styles.ranking}>
                  {showRank(getRank(awayTeam)) && `#${getRank(awayTeam)}`}
                </span>
                <Link
                  data-away="name"
                  href={`/high-school/${awayTeam.urlSlug}/${sport}/scores/`}
                >
                  <figure>
                    <On3Avatar
                      altText={awayTeam?.name}
                      height={25}
                      source={getAssetUrl(awayTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span>{awayTeam?.name}</span>
                </Link>
                <span className={clsx(styles.smallText, styles.record)}>
                  ({awayTeam.record || '0-0'})
                </span>
              </div>
              <div className={styles.home}>
                <span className={styles.ranking}>
                  {showRank(getRank(homeTeam)) && `#${getRank(homeTeam)}`}
                </span>
                <Link
                  data-home="name"
                  href={`/high-school/${homeTeam.urlSlug}/${sport}/scores/`}
                >
                  <figure>
                    <On3Avatar
                      altText={homeTeam?.name}
                      height={25}
                      source={getAssetUrl(homeTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span data-home="name">{homeTeam?.name}</span>
                </Link>
                <span className={clsx(styles.smallText, styles.record)}>
                  ({homeTeam.record || '0-0'})
                </span>
              </div>
            </div>
            <div
              className={styles.scores}
              onClick={gameActions.edit}
              onKeyDown={(e) => handleKeyDown(e)}
              role={gameActions.edit && 'button'}
              tabIndex={gameActions.edit && 0}
            >
              <span className={getScoreStyle(awayTeam)} data-away="score">
                {awayTeam.score ?? '-'}
              </span>
              <span className={getScoreStyle(homeTeam)} data-home="score">
                {homeTeam.score ?? '-'}
              </span>
            </div>
            <div className={styles.predictions}>
              <div>
                <span data-away="prediction">{awayTeam.prediction ?? '-'}</span>
                <span data-home="prediction">{homeTeam.prediction ?? '-'}</span>
              </div>
              {statusEnum === GameStatuses.Completed ? (
                <div
                  className={clsx(styles.result, {
                    [styles.correct]: predictionOutcome,
                    [styles.incorrect]: !predictionOutcome,
                  })}
                >
                  {predictionOutcome ? <Check /> : <XMark />}
                </div>
              ) : (
                <div>
                  <span data-away="winProbability">
                    {decimalToPercentage(awayTeam.winProbability)}
                  </span>
                  <span data-home="winProbability">
                    {decimalToPercentage(homeTeam.winProbability)}
                  </span>
                </div>
              )}
            </div>
          </section>
        </article>
      )}
    </GameWrapper>
  );
};
